import { useReportDateState } from "../hooks/useReportDateAccess";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { localToday } from "../utils/localDate";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSyncAlt } from "react-icons/fa";
import { downloadCSV, downloadExcel, downloadPDF, printThermal, printThermalViaUsb} from "../utils/exportHelpers";
import { formatStoredMoney } from "../utils/currency";
import ReportPeriodPicker from "../components/ReportPeriodPicker";
import ReportPageHeader from "../components/ReportPageHeader";
import ReportControlBar from "../components/ReportControlBar";
import { API_BASE_URL } from "../config/constants";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";
import { getOfflineDayWiseKotReport } from "../offline/financeStore";

const REPORT_ENDPOINT = `${API_BASE_URL}/report-v2/day-wise-kot`;

const COLOR = {
  primary: "var(--app-primary)",
  bg: "#f5f7fb",
  card: "#ffffff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
};

const emptyStatusBucket = () => ({
  pending: 0,
  prepared: 0,
  delivered: 0,
  cancelled: 0,
});

const buildDefaultReport = (value = today()) => ({
  date: value,
  fromdate: value,
  todate: value,
  restaurant: "",
  total_kots: 0,
  total_item_lines: 0,
  total_quantity: 0,
  total_tables: 0,
  status_summary: {
    quantities: emptyStatusBucket(),
    item_lines: emptyStatusBucket(),
  },
  kots: [],
  item_summary: {
    total_items: 0,
    total_quantity: 0,
    total_orders: 0,
    total_amount: 0,
    items: [],
  },
});

const normalizeStatusBucket = (value) => ({
  pending: Number(value?.pending ?? 0),
  prepared: Number(value?.prepared ?? 0),
  delivered: Number(value?.delivered ?? 0),
  cancelled: Number(value?.cancelled ?? 0),
});

const normalizeKotRow = (row) => ({
  kot_id: Number(row?.kot_id ?? 0),
  kot_number: row?.kot_number ? String(row.kot_number) : "",
  display_kot_number: String(
    row?.display_kot_number || row?.kot_number || `KOT-${row?.kot_id || "-"}`
  ),
  order_id: row?.order_id ?? null,
  qr_order_id: row?.qr_order_id ?? null,
  table_no: row?.table_no ?? null,
  waiter_name: row?.waiter_name ? String(row.waiter_name) : "",
  special_notes: row?.special_notes ? String(row.special_notes) : "",
  created_at: row?.created_at ? String(row.created_at) : "",
  item_lines: Number(row?.item_lines ?? 0),
  total_quantity: Number(row?.total_quantity ?? 0),
  overall_status: String(row?.overall_status || "pending"),
  status_quantities: normalizeStatusBucket(row?.status_quantities),
  status_item_lines: normalizeStatusBucket(row?.status_item_lines),
  items: Array.isArray(row?.items)
    ? row.items.map((item) => ({
        item_name: String(item?.item_name || "-"),
        qty: Number(item?.qty ?? 0),
        status: String(item?.status || "pending"),
      }))
    : [],
});

const normalizeItemSummary = (value) => ({
  total_items: Number(value?.total_items ?? 0),
  total_quantity: Number(value?.total_quantity ?? 0),
  total_orders: Number(value?.total_orders ?? 0),
  total_amount: Number(value?.total_amount ?? 0),
  items: Array.isArray(value?.items)
    ? value.items.map((item) => ({
        product_id: Number(item?.product_id ?? 0),
        item_name: String(item?.item_name || "-"),
        category_name: String(item?.category_name || ""),
        total_quantity: Number(item?.total_quantity ?? 0),
        total_orders: Number(item?.total_orders ?? 0),
        total_amount: Number(item?.total_amount ?? 0),
      }))
    : [],
});

const normalizeReport = (result, fromdate, todate) => ({
  date: String(result?.date || fromdate),
  fromdate: String(result?.fromdate || fromdate),
  todate: String(result?.todate || todate),
  restaurant: String(result?.restaurant || ""),
  total_kots: Number(result?.total_kots ?? 0),
  total_item_lines: Number(result?.total_item_lines ?? 0),
  total_quantity: Number(result?.total_quantity ?? 0),
  total_tables: Number(result?.total_tables ?? 0),
  status_summary: {
    quantities: normalizeStatusBucket(result?.status_summary?.quantities),
    item_lines: normalizeStatusBucket(result?.status_summary?.item_lines),
  },
  kots: Array.isArray(result?.kots) ? result.kots.map(normalizeKotRow) : [],
  item_summary: normalizeItemSummary(result?.item_summary),
});

const buildTableMetaMap = (rows = []) => {
  const map = {};
  if (!Array.isArray(rows)) return map;

  rows.forEach((row) => {
    const candidates = [
      row?.id,
      row?.table_id,
      row?.tableid,
      row?.name,
      row?.table_no,
      row?.tableNo,
      row?.tablename,
      row?.table_number,
    ]
      .filter((value) => value !== null && value !== undefined && value !== "")
      .map(String);

    const displayName =
      row?.display_name ||
      row?.table_name ||
      row?.tablename ||
      row?.name ||
      row?.table_no ||
      row?.tableNo ||
      row?.table_number ||
      "";

    const type = String(row?.type || row?.table_type || "").trim();

    candidates.forEach((key) => {
      map[key] = {
        displayName: String(displayName || key),
        type,
      };
    });
  });

  return map;
};

const isTakeawayKey = (value) => {
  const raw = String(value ?? "").trim().toLowerCase();
  return raw === "0" || raw === "-1" || raw.startsWith("takeaway");
};

const DayWiseKOTReportPage = () => {
  const [userId, setUserId] = useState(null);
  const [fromDate, setFromDate] = useReportDateState(today());
  const [toDate, setToDate] = useReportDateState(today());
  const [report, setReport] = useState(buildDefaultReport(today()));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tableMetaByKey, setTableMetaByKey] = useState({});
  const reportRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(user.id);
  }, []);

  useEffect(() => {
    try {
      const rows = JSON.parse(localStorage.getItem("tablelistx") || "[]");
      setTableMetaByKey(buildTableMetaMap(rows));
    } catch {
      setTableMetaByKey({});
    }
  }, []);

  useEffect(() => {
    if (!userId) return;
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fromDate, toDate]);

  const orderedRange = useMemo(() => {
    if (fromDate <= toDate) {
      return { fromdate: fromDate, todate: toDate };
    }
    return { fromdate: toDate, todate: fromDate };
  }, [fromDate, toDate]);

  const fetchReport = async (opts = {}) => {
    if (!userId) return;

    opts.refresh ? setRefreshing(true) : setLoading(true);

    try {
      let result;
      if (isFullOfflineModeEnabled()) {
        result = await getOfflineDayWiseKotReport(
          userId,
          orderedRange.fromdate,
          orderedRange.todate
        );
      } else {
        const response = await fetch(REPORT_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            restaurantid: userId,
            fromdate: orderedRange.fromdate,
            todate: orderedRange.todate,
          }),
        });
        result = await response.json();
        if (!response.ok || !result?.success) {
          throw new Error(result?.message || "Failed to load report");
        }
      }
      setReport(normalizeReport(result, orderedRange.fromdate, orderedRange.todate));
    } catch (error) {
      toast.error(error.message || "Unable to load report");
      setReport(buildDefaultReport(orderedRange.fromdate));
    } finally {
      opts.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const resolveTableLabel = (tableNo) => {
    if (tableNo === null || tableNo === undefined || tableNo === "") {
      return "-";
    }

    if (isTakeawayKey(tableNo)) {
      return "Takeaway";
    }

    const meta = tableMetaByKey[String(tableNo)];
    if (!meta) {
      return `Table ${tableNo}`;
    }

    const typePrefix =
      meta.type && !String(meta.displayName || "").toLowerCase().startsWith(meta.type.toLowerCase())
        ? `${meta.type} `
        : "";

    return `${typePrefix}${meta.displayName}`.trim() || `Table ${tableNo}`;
  };

  const fmtINR = (value) => formatStoredMoney(value, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  const statusEntries = [
    ["Pending", "pending"],
    ["Prepared", "prepared"],
    ["Delivered", "delivered"],
    ["Cancelled", "cancelled"],
  ];

  const exportCsv = () => {
    const rows = report.kots.map((kot, index) => [
      index + 1,
      kot.display_kot_number,
      resolveTableLabel(kot.table_no),
      kot.waiter_name || "-",
      kot.item_lines,
      kot.total_quantity,
      kot.overall_status,
      kot.items.map((item) => `${item.item_name} x ${item.qty} (${item.status})`).join(" | "),
    ]);

    downloadCSV(
      `day_wise_kot_${orderedRange.fromdate}_${orderedRange.todate}.csv`,
      ["#", "KOT", "Table", "Waiter", "Item Lines", "Qty", "Status", "Items"],
      rows
    );
  };

  const exportExcel = () => {
    const rows = report.kots.map((kot, idx) => [
      idx + 1,
      kot.kot_number ? `KOT #${kot.kot_number}` : `#${kot.kot_id}`,
      kot.table_name || "-",
      kot.waiter_name || "-",
      kot.item_lines,
      kot.total_quantity,
      kot.overall_status,
      kot.items.map((item) => `${item.item_name} x ${item.qty} (${item.status})`).join(" | "),
    ]);

    downloadExcel(
      `day_wise_kot_${orderedRange.fromdate}_${orderedRange.todate}.xlsx`,
      ["#", "KOT", "Table", "Waiter", "Item Lines", "Qty", "Status", "Items"],
      rows
    );
  };

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <ReportPageHeader title="Day-wise KOT Report" subtitle="KOT activity summary and item-level details for the selected date range." stats={[{ label: "Total KOTs", value: report.total_kots }, { label: "Item Lines", value: report.total_item_lines }, { label: "Total Quantity", value: report.total_quantity }, { label: "Tables", value: report.total_tables }]} />

      <ReportControlBar>
        <ReportPeriodPicker fromDate={fromDate} toDate={toDate} onChange={({ from, to }) => { setFromDate(from); setToDate(to); }} />
        <PrimaryBtn type="button" onClick={() => fetchReport({ refresh: true })}>
          <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
        </PrimaryBtn>
      </ReportControlBar>

      <Card ref={reportRef}>
        {loading ? (
          <Center>Loading…</Center>
        ) : report.kots.length === 0 ? (
          <Center>No KOT data found for selected date range.</Center>
        ) : (
          <>
            <Section>
              <SectionTitle>Status Summary</SectionTitle>
              <StatusGrid>
                {statusEntries.map(([label, key]) => (
                  <StatusCard key={key}>
                    <strong>{label}</strong>
                    <StatusStat>Qty: {report.status_summary.quantities[key]}</StatusStat>
                    <StatusStat>Lines: {report.status_summary.item_lines[key]}</StatusStat>
                  </StatusCard>
                ))}
              </StatusGrid>
            </Section>

            <Section>
              <SectionTitle>KOT List</SectionTitle>
              <KotList>
                {report.kots.map((kot) => (
                  <KotCard key={`${kot.kot_id}-${kot.display_kot_number}`}>
                    <KotHeader>
                      <div>
                        <KotTitle>{kot.display_kot_number}</KotTitle>
                        <KotMeta>
                          {resolveTableLabel(kot.table_no)} • {kot.waiter_name || "No waiter"}
                        </KotMeta>
                      </div>
                      <StatusBadge $status={kot.overall_status}>
                        {kot.overall_status}
                      </StatusBadge>
                    </KotHeader>
                    <KotInfoRow>
                      <InfoChip>Lines: {kot.item_lines}</InfoChip>
                      <InfoChip>Qty: {kot.total_quantity}</InfoChip>
                      {kot.created_at ? <InfoChip>{formatDateTime(kot.created_at)}</InfoChip> : null}
                    </KotInfoRow>
                    {kot.special_notes ? <KotNotes>Notes: {kot.special_notes}</KotNotes> : null}
                    <MiniTable>
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Qty</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {kot.items.map((item, index) => (
                          <tr key={`${kot.kot_id}-${item.item_name}-${index}`}>
                            <td>{item.item_name}</td>
                            <td>{item.qty}</td>
                            <td>{item.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </MiniTable>
                  </KotCard>
                ))}
              </KotList>
            </Section>

            <Section>
              <SectionTitle>Item Summary</SectionTitle>
              <PillRow>
                <Pill>Items: {report.item_summary.total_items}</Pill>
                <Pill>Qty: {report.item_summary.total_quantity}</Pill>
                <Pill>Orders: {report.item_summary.total_orders}</Pill>
                <Pill>Amount: {fmtINR(report.item_summary.total_amount)}</Pill>
              </PillRow>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Item</th>
                      <th>Category</th>
                      <th>Qty</th>
                      <th>Orders</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.item_summary.items.map((item, index) => (
                      <tr key={`${item.product_id}-${index}`}>
                        <td>{index + 1}</td>
                        <td>{item.item_name}</td>
                        <td>{item.category_name || "Uncategorized"}</td>
                        <td>{item.total_quantity}</td>
                        <td>{item.total_orders}</td>
                        <td>{fmtINR(item.total_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            </Section>
          </>
        )}

        <Actions style={{ marginTop: 10 }}>
          <PrimaryBtn
            type="button"
            onClick={() =>
              downloadPDF(reportRef.current, `day_wise_kot_${orderedRange.fromdate}_${orderedRange.todate}.pdf`)
            }
          >
            PDF
          </PrimaryBtn>
          <PrimaryBtn
            type="button"
            onClick={() =>
              printThermal(reportRef.current, `day_wise_kot_${orderedRange.fromdate}_${orderedRange.todate}`)
            }
          >
            Thermal Print
          </PrimaryBtn><PrimaryBtn
            type="button"
            onClick={() =>
              printThermalViaUsb(reportRef.current, `day_wise_kot_${orderedRange.fromdate}_${orderedRange.todate}`)
            }
          >
            Thermal Print via USB
          </PrimaryBtn>
          <PrimaryBtn type="button" onClick={exportExcel}>
            Excel
          </PrimaryBtn>
          <PrimaryBtn type="button" onClick={exportCsv}>
            CSV
          </PrimaryBtn>
        </Actions>
      </Card>
    </Page>
  );
};

export default DayWiseKOTReportPage;

const today = localToday;

const formatDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value || "");
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Page = styled.div`
  background: radial-gradient(circle at 20% 20%, #f5f8ff, #eef2ff 55%, #f5f7fb 100%);
  min-height: 100vh;
  padding: 18px;
`;

const Top = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  padding: 26px 28px;
  border-radius: 22px;
  background: linear-gradient(135deg,var(--app-primary),var(--app-primary-hover));
  box-shadow:0 14px 30px rgba(var(--app-primary-rgb), .17);
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 900;
  color: #fff;
`;

const Sub = styled.div`
  color: #dff3ff;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const DateInput = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: #f9f9f9;
  color: ${COLOR.text};

  input {
    border: 0;
    background: transparent;
    outline: none;
  }
`;

const PrimaryBtn = styled.button`
  background: ${COLOR.primary};
  color: #fff;
  border: 0;
  border-radius: 10px;
  padding: 10px 12px;
  font-weight: 800;
  cursor: pointer;
  display: inline-flex;
  gap: 8px;
  align-items: center;
`;

const Card = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 14px;
  padding: 12px;
`;

const Center = styled.div`
  text-align: center;
  color: ${COLOR.muted};
  padding: 32px 16px;
`;

const Section = styled.section`
  display: grid;
  gap: 12px;
  margin-bottom: 18px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 1rem;
  color: ${COLOR.text};
`;

const PillRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Pill = styled.span`
  background: rgba(255,255,255,.14);
  color: #fff;
  padding: 8px 10px;
  border-radius: 12px;
  font-weight: 800;
  border: 1px solid rgba(255,255,255,.25);
`;

const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
`;

const StatusCard = styled.div`
  border: 1px solid ${COLOR.line};
  border-radius: 14px;
  padding: 12px;
  background: #f8fafc;
  display: grid;
  gap: 6px;
`;

const StatusStat = styled.div`
  color: ${COLOR.muted};
  font-size: 0.95rem;
`;

const KotList = styled.div`
  display: grid;
  gap: 12px;
`;

const KotCard = styled.div`
  border: 1px solid ${COLOR.line};
  border-radius: 16px;
  padding: 12px;
  background: #fff;
`;

const KotHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const KotTitle = styled.div`
  font-weight: 900;
  color: ${COLOR.text};
`;

const KotMeta = styled.div`
  color: ${COLOR.muted};
  margin-top: 4px;
  font-size: 0.95rem;
`;

const KotInfoRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const InfoChip = styled.span`
  font-size: 0.85rem;
  border-radius: 999px;
  background: var(--app-primary-soft);
  border: 1px solid var(--app-primary-soft);
  color: var(--app-primary);
  padding: 6px 10px;
`;

const KotNotes = styled.div`
  color: ${COLOR.text};
  margin-bottom: 10px;
`;

const StatusBadge = styled.span`
  text-transform: capitalize;
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 0.85rem;
  font-weight: 800;
  background: ${(props) => {
    switch (props.$status) {
      case "prepared":
        return "#fef3c7";
      case "delivered":
        return "#dcfce7";
      case "cancelled":
        return "#fee2e2";
      default:
        return "var(--app-primary-soft)";
    }
  }};
  color: ${(props) => {
    switch (props.$status) {
      case "prepared":
        return "#92400e";
      case "delivered":
        return "#166534";
      case "cancelled":
        return "#991b1b";
      default:
        return "var(--app-primary)";
    }
  }};
`;

const TableWrap = styled.div`
  overflow: auto;
  border: 1px solid ${COLOR.line};
  border-radius: 14px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 720px;

  th,
  td {
    border-bottom: 1px solid ${COLOR.line};
    padding: 12px;
    text-align: left;
    font-size: 0.95rem;
  }

  th {
    background: var(--app-primary-soft);
    color: ${COLOR.text};
    font-weight: 800;
  }
`;

const MiniTable = styled(Table)`
  min-width: 0;
  margin-top: 6px;

  th,
  td {
    padding: 10px;
    font-size: 0.9rem;
  }
`;
